import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getCategoryType, predefinedCategories } from '@/lib/categories';
import { frequencies } from '@/lib/frequencies';
import { formatNumberWithCommas, parseNumberWithCommas } from '@/lib/utils';
import FinancialService, {
  type FinancialRecord,
} from '@/services/FinancialService';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const financialSchema = z
  .object({
    title: z.string().optional(),
    amount: z.coerce.number().min(0, 'Amount must be positive'),
    frequency: z.enum(frequencies.map((f) => f.value) as [string, ...string[]]),
    nextDate: z.string().min(1, 'Next date is required'),
    category: z.string().min(1, 'Category is required'),
  })
  .refine(
    (data) => {
      if (
        data.category === 'OTHER_INCOME' ||
        data.category === 'OTHER_EXPENSE'
      ) {
        return !!data.title;
      }
      return true;
    },
    {
      message: 'Title is required for Other Income/Expense',
      path: ['title'],
    },
  );

type FinancialFormValues = z.infer<typeof financialSchema>;

interface EditFinancialFormProps {
  assetId: string;
  financial: FinancialRecord;
  onSuccess: () => void;
  onCancel: () => void;
}

export function EditFinancialForm({
  assetId,
  financial,
  onSuccess,
  onCancel,
}: EditFinancialFormProps) {
  const form = useForm<FinancialFormValues>({
    resolver: zodResolver(financialSchema),
    defaultValues: {
      title: financial.details || '',
      amount: financial.amount,
      frequency: financial.frequency,
      category: financial.category,
      nextDate:
        financial.due_dates?.[0] || new Date().toISOString().split('T')[0],
    },
  });

  const handleSubmit = async (data: FinancialFormValues) => {
    try {
      const type = getCategoryType(data.category);

      const updatedFinancial: Omit<
        FinancialRecord,
        'id' | 'asset_id' | 'created_at' | 'updated_at'
      > = {
        amount: Number(data.amount),
        frequency: data.frequency as FinancialRecord['frequency'],
        type: type as FinancialRecord['type'],
        category: data.category,
        is_percent: false,
        notify: false,
        due_dates: [data.nextDate],
        details: data.title,
      };

      await FinancialService.updateFinancial(
        assetId,
        financial.id,
        updatedFinancial,
      );
      toast.success('Financial record updated successfully');
      onSuccess();
    } catch (err) {
      console.error('Error updating financial record:', err);
      if (err instanceof Error) {
        try {
          const errorData = JSON.parse(err.message);
          if (errorData.details) {
            for (const detail of errorData.details) {
              const field = detail.path[0] as keyof FinancialFormValues;
              form.setError(field, {
                type: 'manual',
                message: detail.message,
              });
            }
            toast.error('Please fix the validation errors');
            return;
          }
        } catch {
          toast.error(err.message || 'Failed to update financial record');
        }
      } else {
        toast.error('Failed to update financial record');
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="0.00"
                      className="pl-8"
                      {...field}
                      onChange={(e) => {
                        const value = parseNumberWithCommas(e.target.value);
                        field.onChange(value);
                      }}
                      value={formatNumberWithCommas(field.value || 0)}
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="frequency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Frequency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {frequencies.map((freq) => (
                      <SelectItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {predefinedCategories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="nextDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Next Date</FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    {...field}
                    value={
                      field.value || new Date().toISOString().split('T')[0]
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {['OTHER_INCOME', 'OTHER_EXPENSE'].includes(form.watch('category')) && (
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="flex gap-3">
          <Button type="submit" className="flex-1">
            Save Changes
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
